import React, { useState, useEffect } from "react";
import { useLocation, Route, Redirect, Switch } from "react-router-dom";
import Dashboard from "./components/Main_Page/mainHomepage";
import Login from "./Login/Login";
import PasswordReset from "./Login/passwordReset";
import MFAOtp from "./components/MFA/MFAOtp";
import VerifyAccount from "./registration/VerifyAccount";
import { useHistory } from "react-router-dom";
import { BASE_URL } from "./App_Settings/app_settings";
import { useSharedValue } from "../src/components/Constants/constants";
// SECURITY FIX: Public self-registration disabled.
// import Registration from "./registration/registration";

// Protected Route Component
const ProtectedRoute = ({ component: Component, isLoggedIn, someValue, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      isLoggedIn || someValue === true ? <Component {...props} /> : <Redirect to="/" />
    }
  />
);

const MainApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const history = useHistory();
  const { isDirectNavigation, updateDirectNavigation } = useSharedValue();
  let someValue = location.state ? location.state.someValue : false;

  // Check authentication status immediately on component mount
  useEffect(() => {
    const checkAuthentication = () => {
      const userDataJSON = localStorage.getItem("token");
      if (userDataJSON) {
        try {
          const userData = JSON.parse(userDataJSON);
          if (userData && userData.userId) {
            setIsLoggedIn(true);
          } else {
            setIsLoggedIn(false);
            localStorage.removeItem("token");
            localStorage.removeItem("sessionToken");
          }
        } catch (error) {
          console.error("Invalid token in localStorage:", error);
          setIsLoggedIn(false);
          localStorage.removeItem("token");
          localStorage.removeItem("sessionToken");
        }
      } else {
        setIsLoggedIn(false);
      }
      setIsLoading(false);
    };

    checkAuthentication();
  }, []);

  // Session heartbeat — check every 30 seconds if session is still valid
  useEffect(() => {
    if (!isLoggedIn) return;

    const checkSession = async () => {
      const tokenData = localStorage.getItem("token");
      const sessionToken = localStorage.getItem("sessionToken");
      if (!tokenData || !sessionToken) return;

      try {
        const userData = JSON.parse(tokenData);
        const response = await fetch(`${BASE_URL}/api/SessionManagement/validate`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "X-Session-Token": sessionToken,
          },
        });

        if (response.status === 401) {
          // Session was terminated (force login from another device)
          localStorage.removeItem("token");
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("CopilotToken");
          alert(
            "Your session has been terminated because you logged in from another device. Please login again."
          );
          window.location.href = "/login";
          return;
        }

        // Also check response body for isValid: false
        try {
          const data = await response.json();
          if (data && data.isValid === false) {
            localStorage.removeItem("token");
            localStorage.removeItem("sessionToken");
            localStorage.removeItem("CopilotToken");
            alert(
              "Your session has been terminated because you logged in from another device. Please login again."
            );
            window.location.href = "/login";
          }
        } catch (e) {
          // ignore parse errors
        }
      } catch (error) {
        // Network error — don't log out, just skip this check
        console.warn("Session heartbeat check failed:", error);
      }
    };

    // Check immediately and then every 30 seconds
    checkSession();
    const intervalId = setInterval(checkSession, 30000);

    return () => clearInterval(intervalId);
  }, [isLoggedIn]);

  useEffect(() => {
    const isValidUUID = (id) => {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(id);
    };

    const getIdsFromUrl = () => {
      const url = window.location.href;
      const [urlPath, queryString] = url.split("?");
      const segments = urlPath.split("/").filter((segment) => segment.trim() !== "");

      // Extract SSO token and timestamp from query parameters
      const params = new URLSearchParams(queryString || "");
      const ssoToken = params.get("sso_token") || null;
      const ssoTimestamp = params.get("timestamp") || null;

      // Extract the last two segments as potential UUIDs
      const lastSegment = segments[segments.length - 1];
      const secondLastSegment = segments[segments.length - 2];

      // Check if we have two valid UUIDs at the end
      if (isValidUUID(lastSegment) && isValidUUID(secondLastSegment)) {
        return {
          organizationId: secondLastSegment,
          userId: lastSegment,
          ssoToken,
          ssoTimestamp,
        };
      }

      // Fallback: check if only the last segment is a valid UUID (old format)
      if (isValidUUID(lastSegment)) {
        return {
          organizationId: null,
          userId: lastSegment,
          ssoToken,
          ssoTimestamp,
        };
      }

      return { organizationId: null, userId: null, ssoToken: null, ssoTimestamp: null };
    };

    const { organizationId, userId, ssoToken, ssoTimestamp } = getIdsFromUrl();

    // Check if we have valid IDs from URL
    if (userId && userId.trim() !== "") {
      // SECURITY FIX: Require SSO token for auto-login (prevents unauthenticated IDOR)
      if (!ssoToken || !ssoTimestamp) {
        console.error("SSO auto-login requires sso_token and timestamp parameters.");
        updateDirectNavigation(false);
        return;
      }

      // Set direct navigation to true immediately for auto-login
      updateDirectNavigation(true);

      const validateUser = async () => {
        try {
          // Build API URL with HMAC-signed SSO token parameters
          const ssoParams = `?sso_token=${encodeURIComponent(ssoToken)}&timestamp=${encodeURIComponent(ssoTimestamp)}`;
          const apiUrl = organizationId
            ? `${BASE_URL}/Users/validateuser/${organizationId}/${userId}${ssoParams}`
            : `${BASE_URL}/Users/validateuser/${userId}${ssoParams}`;

          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            alert(`HTTP error! Status: ${response.status}`);
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json();

          if (data.userId !== undefined) {
            // Store the token from validateuser API response
            localStorage.setItem("token", JSON.stringify(data));

            const currentTime = new Date();
            localStorage.setItem("loginTime", currentTime);

            // Ensure direct navigation remains true
            updateDirectNavigation(true);
            setIsLoggedIn(true);

            // Always redirect to dashboard for auto-login
            history.push({
              pathname: "/dashboard",
              state: { someValue: true },
            });
          } else {
            alert("Invalid user credentials. Please try again.");
            // Reset direct navigation on failure
            updateDirectNavigation(false);
          }
        } catch (error) {
          console.error("Error during authentication:", error);
          alert("Authentication failed. Please try again.");
          // Reset direct navigation on error
          updateDirectNavigation(false);
        }
      };

      validateUser();
    } else {
      // console.log("No valid UUID in URL, skipping auto-login");
    }
  }, [history, updateDirectNavigation]); // Adding dependencies

  // Update authentication state when someValue changes
  useEffect(() => {
    if (someValue === true) {
      setIsLoggedIn(true);
    }
  }, [someValue]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Switch>
      <Route exact path="/login" render={() => <Login />} />
      <Route exact path="/mfa-verify" render={(props) => <MFAOtp {...props} />} />
      <Route exact path="/homePage" render={() => <Dashboard />} />
      {/* SECURITY FIX: Public self-registration disabled. New users created only by admins via User Profile. */}
      {/* <Route exact path="/registration" component={() => <Registration />} /> */}
      <Route exact path="/verify-account" component={VerifyAccount} />
      <Route exact path="/passwordReset" component={PasswordReset} />

      {/* Protected Routes - Redirect to root if not authenticated */}
      <ProtectedRoute
        path="/dashboard"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/workloadFileImport"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/bulkFormImport"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/displayAndEditFile"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/displayExtractionView"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/extractionReview"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/userRoleProfile"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/businessUnit"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/systemLink"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/organisation"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/export"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/approval"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/reports"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/approvalReviewProcess"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/home"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/rejections"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/extractionApproval"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/billing"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/userSettings"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/invoice"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />

      {/* Catch-all route - redirect any unmatched authenticated routes to dashboard, unauthenticated to root */}
      <Route
        render={() =>
          isLoggedIn || someValue === true ? <Redirect to="/dashboard" /> : <Redirect to="/" />
        }
      />
    </Switch>
  );
};

export default MainApp;
